import {
	IconBold,
	IconClearFormatting,
	IconCode,
	IconHighlight,
	IconItalic,
	IconMathFunction,
	IconSearch,
	IconSquareRoot,
	IconStrikethrough,
	IconSubscript,
	IconSum,
	IconSuperscript,
	IconUnderline,
} from "@tabler/icons-react";
import type { Editor } from "@tiptap/core";
import type { ComponentType } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOptionalEditorContext } from "@/contexts/EditorContext";
import { cn } from "@/lib/cn";
import { dispatchToolboxInsert } from "@/components/toolbox/insertHandler";
import { MegaFooter, readShowLatexPref, writeShowLatexPref } from "./MegaFooter";

// ds27 §6/§7 — Format mega menu (calm "Mega menu" model). Mirrors InsertMenu
// 1:1 (portal under the trigger, backdrop, search, grouped grid, Escape/keyboard,
// fixed-anchor positioning) so the ribbon's overflow-x clip can't cut it off.
//
// Two columns: Text · Code & math.
//
// The Paragraph column is gone — every item in it was scoped to the block,
// not the selection, and duplicated `¶ Body` exactly. This menu now owns one
// thing: what is true of the current SELECTION.
// Design: lilia-docs/design-handoffs/2026-09-11-editor-chrome/ §8
//
// Every command REUSES the existing working implementations — EditorContext for
// the canonical marks + inline math, the same `editor.chain().focus()….run()`
// calls the previous ribbon used, and `dispatchToolboxInsert` for the block
// kinds (display equation / code block) that are custom Flow block nodes.

interface FormatMenuProps {
	open: boolean;
	onClose: () => void;
	/** Active TipTap editor — drives node-level commands + active state. */
	editor?: Editor | null;
	/** The trigger button — the menu is portalled to <body> and anchored
	 *  under it (fixed), so the ribbon's overflow-x clip can't cut it off. */
	anchorEl?: HTMLElement | null;
}

const MENU_W = 560;

type ColumnKey = "text" | "code-math" | "paragraph";

interface FormatItem {
	/** Stable slug → drives the data-testid. */
	testId: string;
	label: string;
	hint?: string;
	shortcut?: string;
	/** Representative LaTeX this item emits — previewed in the mega footer
	 *  (ds28 §3.4). Empty/undefined → footer shows the placeholder. */
	latex?: string;
	icon: ComponentType<{ size?: number | string; className?: string }>;
	/** Whether the mark/node is currently active (drives the active style). */
	isActive?: (editor: Editor) => boolean;
	/** Run the command. Closes the menu afterwards. */
	run: (api: FormatApi) => void;
}

interface FormatColumn {
	key: ColumnKey;
	label: string;
	items: FormatItem[];
}

/** Everything an item's `run` needs — the editor + the EditorContext marks. */
interface FormatApi {
	editor: Editor | null;
	ctx: ReturnType<typeof useOptionalEditorContext>;
}

// ──────────────────────────────────────────────────────────────────────
// Greek / operators quick-insert (reused verbatim from the previous ribbon's
// `math-symbol` button). Inserts the LaTeX wrapped in inline math so it
// round-trips through the existing math pipeline.
// ──────────────────────────────────────────────────────────────────────
const SYMBOL_GROUPS: { label: string; symbols: { glyph: string; latex: string }[] }[] = [
	{
		label: "Greek",
		symbols: [
			{ glyph: "α", latex: "\\alpha" },
			{ glyph: "β", latex: "\\beta" },
			{ glyph: "γ", latex: "\\gamma" },
			{ glyph: "δ", latex: "\\delta" },
			{ glyph: "ε", latex: "\\epsilon" },
			{ glyph: "θ", latex: "\\theta" },
			{ glyph: "λ", latex: "\\lambda" },
			{ glyph: "μ", latex: "\\mu" },
			{ glyph: "π", latex: "\\pi" },
			{ glyph: "σ", latex: "\\sigma" },
			{ glyph: "φ", latex: "\\phi" },
			{ glyph: "ω", latex: "\\omega" },
			{ glyph: "Γ", latex: "\\Gamma" },
			{ glyph: "Δ", latex: "\\Delta" },
			{ glyph: "Σ", latex: "\\Sigma" },
			{ glyph: "Ω", latex: "\\Omega" },
		],
	},
	{
		label: "Operators & relations",
		symbols: [
			{ glyph: "×", latex: "\\times" },
			{ glyph: "÷", latex: "\\div" },
			{ glyph: "±", latex: "\\pm" },
			{ glyph: "·", latex: "\\cdot" },
			{ glyph: "≤", latex: "\\leq" },
			{ glyph: "≥", latex: "\\geq" },
			{ glyph: "≠", latex: "\\neq" },
			{ glyph: "≈", latex: "\\approx" },
			{ glyph: "∞", latex: "\\infty" },
			{ glyph: "∑", latex: "\\sum" },
			{ glyph: "∏", latex: "\\prod" },
			{ glyph: "∫", latex: "\\int" },
			{ glyph: "∂", latex: "\\partial" },
			{ glyph: "∇", latex: "\\nabla" },
			{ glyph: "√", latex: "\\sqrt{}" },
			{ glyph: "→", latex: "\\to" },
		],
	},
];

// Text colors — names match xcolor's [dvipsnames,svgnames] so they round-trip
// into LaTeX without `\definecolor`. Kept in sync with the previous ribbon.
const TEXT_COLORS = [
	{ name: "red", hex: "#ef4444" },
	{ name: "orange", hex: "#f97316" },
	{ name: "amber", hex: "#f59e0b" },
	{ name: "yellow", hex: "#eab308" },
	{ name: "lime", hex: "#84cc16" },
	{ name: "green", hex: "#22c55e" },
	{ name: "teal", hex: "#14b8a6" },
	{ name: "cyan", hex: "#06b6d4" },
	{ name: "sky", hex: "#0ea5e9" },
	{ name: "blue", hex: "#3b82f6" },
	{ name: "indigo", hex: "#6366f1" },
	{ name: "violet", hex: "#8b5cf6" },
	{ name: "purple", hex: "#a855f7" },
	{ name: "pink", hex: "#ec4899" },
	{ name: "rose", hex: "#f43f5e" },
	{ name: "black", hex: "#000000" },
	{ name: "gray", hex: "#6b7280" },
	{ name: "darkgray", hex: "#374151" },
];


// ──────────────────────────────────────────────────────────────────────
// Column definitions
// ──────────────────────────────────────────────────────────────────────
const COLUMNS: FormatColumn[] = [
	{
		key: "text",
		label: "Text",
		items: [
			{
				testId: "format-bold",
				label: "Bold",
				shortcut: "⌘B",
				latex: "\\textbf{…}",
				icon: IconBold,
				isActive: (e) => e.isActive("bold"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleBold() : editor?.chain().focus().toggleMark("bold").run(),
			},
			{
				testId: "format-italic",
				label: "Italic",
				shortcut: "⌘I",
				latex: "\\textit{…}",
				icon: IconItalic,
				isActive: (e) => e.isActive("italic"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleItalic() : editor?.chain().focus().toggleMark("italic").run(),
			},
			{
				testId: "format-underline",
				label: "Underline",
				shortcut: "⌘U",
				latex: "\\underline{…}",
				icon: IconUnderline,
				isActive: (e) => e.isActive("underline"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleUnderline() : editor?.chain().focus().toggleMark("underline").run(),
			},
			{
				testId: "format-strikethrough",
				label: "Strikethrough",
				latex: "\\sout{…}",
				icon: IconStrikethrough,
				isActive: (e) => e.isActive("strike"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleStrike() : editor?.chain().focus().toggleMark("strike").run(),
			},
			{
				testId: "format-superscript",
				label: "Superscript",
				latex: "^{…}",
				icon: IconSuperscript,
				isActive: (e) => e.isActive("superscript"),
				run: ({ editor }) => editor?.chain().focus().toggleMark("superscript").run(),
			},
			{
				testId: "format-subscript",
				label: "Subscript",
				latex: "_{…}",
				icon: IconSubscript,
				isActive: (e) => e.isActive("subscript"),
				run: ({ editor }) => editor?.chain().focus().toggleMark("subscript").run(),
			},
			{
				testId: "format-highlight",
				label: "Highlight",
				latex: "\\hl{…}",
				icon: IconHighlight,
				isActive: (e) => e.isActive("highlight"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleHighlight() : editor?.chain().focus().toggleMark("highlight").run(),
			},
			{
				testId: "format-code",
				label: "Inline code",
				latex: "\\texttt{…}",
				icon: IconCode,
				isActive: (e) => e.isActive("code"),
				run: ({ editor, ctx }) =>
					ctx ? ctx.toggleCode() : editor?.chain().focus().toggleMark("code").run(),
			},
			{
				testId: "format-clear",
				label: "Clear formatting",
				latex: "removes formatting",
				icon: IconClearFormatting,
				run: ({ editor }) => editor?.chain().focus().unsetAllMarks().run(),
			},
		],
	},
	{
		key: "code-math",
		label: "Code & math",
		items: [
			{
				testId: "math-inline",
				label: "Inline math",
				hint: "$…$",
				latex: "$…$",
				icon: IconMathFunction,
				run: ({ editor, ctx }) =>
					ctx ? ctx.insertInlineMath() : editor?.chain().focus().insertContent("$E=mc^2$").run(),
			},
			{
				testId: "math-display",
				label: "Display equation",
				hint: "$$…$$",
				latex: "\\[ … \\]",
				icon: IconSquareRoot,
				// Display equation is a real Flow block node — route through the
				// shared toolbox dispatch (same as Insert menu's Equation item).
				run: () => dispatchToolboxInsert({ kind: "equation" }, "toolbox"),
			},
			// `math-symbol` is rendered inline below the column (Greek/operators
			// grid) — listed here in the data only as a search anchor.
			{
				testId: "math-symbol",
				label: "Insert symbol",
				hint: "Greek / operators",
				latex: "\\alpha …",
				icon: IconSum,
				// No-op run: the actual insert happens via the inline symbol grid;
				// clicking the row just keeps the menu open near its grid.
				run: () => undefined,
			},
		],
	},
];

export function FormatMenu({ open, onClose, editor, anchorEl }: FormatMenuProps) {
	const ctx = useOptionalEditorContext();
	const [query, setQuery] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	// ds28 §3.4 — LaTeX preview footer: the snippet of the hovered/focused item
	// + the persisted "Show LaTeX" preference.
	const [hoveredLatex, setHoveredLatex] = useState("");
	const [showLatex, setShowLatex] = useState(readShowLatexPref);
	// A tick that increments on every editor transaction so active-state in the
	// menu stays fresh while it's open.
	const [, setTick] = useState(0);

	// Anchor position (fixed coords under the trigger), clamped to the viewport.
	const [pos, setPos] = useState<{ top: number; left: number }>({ top: 56, left: 12 });
	useEffect(() => {
		if (!open || !anchorEl) return;
		const r = anchorEl.getBoundingClientRect();
		const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8));
		setPos({ top: r.bottom + 4, left });
	}, [open, anchorEl]);

	useEffect(() => {
		if (!open) return;
		setQuery("");
		setHoveredLatex("");
		const t = setTimeout(() => searchRef.current?.focus(), 30);
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => {
			clearTimeout(t);
			window.removeEventListener("keydown", onKey);
		};
	}, [open, onClose]);

	useEffect(() => {
		if (!open || !editor) return;
		const refresh = () => setTick((t) => t + 1);
		editor.on("selectionUpdate", refresh);
		editor.on("transaction", refresh);
		return () => {
			editor.off("selectionUpdate", refresh);
			editor.off("transaction", refresh);
		};
	}, [open, editor]);

	const columns = useMemo(() => {
		const q = query.trim().toLowerCase();
		return COLUMNS.map((col) => ({
			...col,
			items: col.items.filter(
				(it) =>
					!q ||
					it.label.toLowerCase().includes(q) ||
					(it.hint ?? "").toLowerCase().includes(q),
			),
		}));
	}, [query]);

	const isEmpty = columns.every((c) => c.items.length === 0);

	if (!open) return null;

	const api: FormatApi = { editor: editor ?? null, ctx };

	const activate = (it: FormatItem) => {
		it.run(api);
		// `math-symbol` keeps the menu open (its grid is the action surface).
		if (it.testId === "math-symbol") return;
		onClose();
	};

	const insertSymbol = (latex: string) => {
		if (ctx) ctx.insertInlineMath(latex);
		else editor?.chain().focus().insertContent(`$${latex}$`).run();
		onClose();
	};

	const applyTextColor = (color: string) => {
		if (!editor) {
			onClose();
			return;
		}
		const { from, to } = editor.state.selection;
		if (from !== to) editor.chain().focus().setMark("textColor", { color }).run();
		onClose();
	};


	return createPortal(
		<>
			<div data-testid="format-backdrop" className="fixed inset-0 z-[60]" onClick={onClose} />
			<div
				data-testid="format-menu"
				role="menu"
				aria-label="Format"
				className="fixed z-[61] overflow-hidden rounded-[14px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]"
				style={{ top: pos.top, left: pos.left, width: MENU_W, maxWidth: "92vw", boxShadow: "0 22px 60px var(--lilia-shadow-lg)" }}
			>
				{/* Search */}
				<div className="flex items-center gap-2 border-b border-[var(--lilia-border)] px-3 h-10">
					<IconSearch size={15} className="shrink-0 text-[var(--lilia-text-muted)]" />
					<input
						ref={searchRef}
						data-testid="format-menu-search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Filter formatting…"
						className="w-full bg-transparent text-[13px] text-[var(--lilia-text)] outline-none placeholder:text-[var(--lilia-text-muted)]"
					/>
				</div>

				{isEmpty ? (
					<div
						data-testid="format-menu-empty"
						className="px-4 py-10 text-center text-[13px] text-[var(--lilia-text-muted)]"
					>
						No formatting matches “{query}”.
					</div>
				) : (
					<div
						className="grid grid-cols-3 gap-x-2 gap-y-3 max-h-[60vh] overflow-y-auto p-3"
						onMouseLeave={() => setHoveredLatex("")}
					>
						{columns.map((col) => (
							<div key={col.key} className="min-w-0">
								{col.items.length > 0 && (
									<div className="px-2 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
										{col.label}
									</div>
								)}
								{col.items.map((it) => {
									const Icon = it.icon;
									const active = editor && it.isActive ? it.isActive(editor) : false;
									return (
										<button
											key={it.testId}
											type="button"
											role="menuitem"
											data-testid={it.testId}
											data-active={active ? "true" : "false"}
											aria-pressed={it.isActive ? active : undefined}
											onClick={() => activate(it)}
											onMouseEnter={() => setHoveredLatex(it.latex ?? "")}
											onFocus={() => setHoveredLatex(it.latex ?? "")}
											className={cn(
												"relative flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors cursor-pointer",
												active
													? "bg-[var(--lilia-primary-subtle)] text-[var(--lilia-primary)]"
													: "hover:bg-[var(--lilia-hover)] text-[var(--lilia-text)]",
											)}
										>
											<Icon
												size={16}
												className={cn(
													"shrink-0",
													active
														? "text-[var(--lilia-primary)]"
														: "text-[var(--lilia-text-secondary)]",
												)}
											/>
											<span className="min-w-0 flex-1">
												<span className="flex items-center gap-1.5">
													<span className="text-[13px] font-medium">{it.label}</span>
												</span>
												{it.hint && (
													<span className="block truncate text-[11px] text-[var(--lilia-text-muted)]">
														{it.hint}
													</span>
												)}
											</span>
											{it.shortcut && (
												<span className="shrink-0 text-[10.5px] tabular-nums text-[var(--lilia-text-muted)]">
													{it.shortcut}
												</span>
											)}
											{active && (
												<span
													aria-hidden="true"
													className="absolute left-2 right-2 bottom-0.5 h-0.5 rounded-full bg-[var(--lilia-primary)]"
												/>
											)}
										</button>
									);
								})}

								{/* Inline sub-pickers under their column (only when not filtered out) */}
								{col.key === "code-math" &&
									!query.trim() &&
									SYMBOL_GROUPS.map((group) => (
										<div key={group.label} className="mt-2 px-2">
											<div className="pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
												{group.label}
											</div>
											<div className="grid grid-cols-8 gap-0.5">
												{group.symbols.map((s) => (
													<button
														key={s.latex}
														type="button"
														data-testid="math-symbol-item"
														data-latex={s.latex}
														title={s.latex}
														onClick={() => insertSymbol(s.latex)}
														onMouseEnter={() => setHoveredLatex(s.latex)}
														onFocus={() => setHoveredLatex(s.latex)}
														className="h-7 w-7 inline-flex items-center justify-center rounded text-[15px] text-[var(--lilia-text)] hover:bg-[var(--lilia-hover)] cursor-pointer"
													>
														{s.glyph}
													</button>
												))}
											</div>
										</div>
									))}

								{col.key === "paragraph" && !query.trim() && (
									<>
										{/* Text color swatch row */}
										<div className="mt-2 px-2">
											<div className="pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
												Text color
											</div>
											<div className="grid grid-cols-6 gap-1" data-testid="format-text-color">
												{TEXT_COLORS.map((c) => (
													<button
														key={c.name}
														type="button"
														data-testid="format-text-color-swatch"
														data-color={c.name}
														title={c.name}
														aria-label={`Text color ${c.name}`}
														onClick={() => applyTextColor(c.name)}
														onMouseEnter={() => setHoveredLatex(`\\textcolor{${c.name}}{…}`)}
														onFocus={() => setHoveredLatex(`\\textcolor{${c.name}}{…}`)}
														className="w-5 h-5 rounded-full border border-[var(--lilia-border)] hover:scale-110 transition-transform cursor-pointer"
														style={{ background: c.hex }}
													/>
												))}
											</div>
										</div>
									</>
								)}
							</div>
						))}
					</div>
				)}

				{/* ds28 §3.4 — LaTeX preview footer pinned to the bottom. */}
				<MegaFooter
					hoveredLatex={hoveredLatex}
					showLatex={showLatex}
					onToggle={(next) => {
						setShowLatex(next);
						writeShowLatexPref(next);
					}}
				/>
			</div>
		</>,
		document.body,
	);
}
